import AdviserDetailsCard from "./AdviserDetailsCard";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getDoc } from "firebase/firestore";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import ButtonTextLink from "../../../DesignSystem/Buttons/ButtonTextLink";
import Field from "../../../DesignSystem/Inputs/Field";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import CheckboxMultiSelect from "../../../DesignSystem/Dropdowns/CheckboxMultiSelect";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import Tabs from "../../../DesignSystem/Tabs/Tabs";
import { PostCard } from "../HelperFunctions/HelperFunctions";
import { RATE_FIELDS, normaliseRates } from "./rateFields";
import {
  calculateTotalMonthlyFees,
  loadPayingForRows,
  saveAdviserRates,
} from "./ratesService";
import RatesEditLogs from "./RatesEditLogs";
import { asList } from "../../Utils/AdviserFormatters";
import { useAdviserList } from "../../../../context/Adviser/Utils/AdviserEmailList";
import {
  getIODetailsRef,
  subscribeIODetails,
} from "../IODetails/ioDetailsService";
import { getSavedGroupDetails } from "../../../GroupDetails/groupDetailsService";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import { canAccessDepartmentRoute } from "../../../Departments/utils/departmentConfig";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { statementPasswordFromDob } from "../../../Accounts/MonthlyPayments/utils/statementPassword";
import {
  isValidStatementCc,
  normaliseStatementCc,
} from "./statementRecipients";
import {
  MONTHLY_FEE_OPTIONS,
  monthlyFeeAmountForPermission,
  resolveMonthlyFeePermission,
} from "./monthlyFeeOptions";
import {
  toMonthlyFeeDateValue,
  toMonthlyFeeMonthValue,
} from "../../../Accounts/MonthlyPayments/utils/monthlyFeeGuideline";
import "../../../../css/Adviser/EditAdviserDetailsPage.css";

const EDIT_RATE_FIELDS = [
  ...RATE_FIELDS,
  { key: "paidBy", label: "Paid By", type: "text" },
  { key: "notes", label: "Notes", type: "text" },
];

const BANK_DETAIL_FIELDS = [
  {
    key: "adviserSortCode",
    label: "Sort Code",
    placeholder: "00-00-00",
    maxLength: 8,
    pattern: "\\d{2}-\\d{2}-\\d{2}",
    inputMode: "numeric",
  },
  {
    key: "adviserAccountNumber",
    label: "Account Number",
    placeholder: "xxxxxxxx",
    maxLength: 8,
    pattern: "\\d{8}",
    inputMode: "numeric",
  },
  {
    key: "adviserAccountHolder",
    label: "Account Holder",
    placeholder: "Account Name",
    full: true,
  },
  {
    key: "arSortCode",
    label: "Sort Code",
    placeholder: "00-00-00",
    maxLength: 8,
    pattern: "\\d{2}-\\d{2}-\\d{2}",
    inputMode: "numeric",
  },
  {
    key: "arAccountNumber",
    label: "Account Number",
    placeholder: "xxxxxxxx",
    maxLength: 8,
    pattern: "\\d{8}",
    inputMode: "numeric",
  },
  {
    key: "arAccountHolder",
    label: "Account Holder",
    placeholder: "Account Name",
    full: true,
  },
];

const PASSWORD_DETAIL_FIELDS = [
  {
    key: "statementPassword",
    label: "Statement Password",
    placeholder: "Enter statement password",
  },
];

const DEFAULT_STATEMENT_EMAIL = "accounts@rosemountfs.co.uk";

const COMMISSION_SELECT_FIELDS = [
  {
    key: "sendCommissionPaymentsTo",
    label: "Adviser to send commission payments",
    placeholder: "Adviser Name / Account Name",
  },
  {
    key: "emailCommissionStatementsTo",
    label: "Send Details",
    placeholder: "Adviser Name / Email",
  },
];

const EDIT_TABS = [
  { id: "rates", label: "IFA Rates" },
  { id: "bankDetails", label: "Bank & Password Details" },
];

const formatSortCode = (value = "") =>
  String(value)
    .replace(/\D/g, "")
    .slice(0, 6)
    .replace(/(\d{2})(?=\d)/g, "$1-");

const formatAccountNumber = (value = "") =>
  String(value).replace(/\D/g, "").slice(0, 8);

const formatBankDetailValue = (key, value) => {
  if (key.endsWith("SortCode")) return formatSortCode(value);
  if (key.endsWith("AccountNumber")) return formatAccountNumber(value);
  return value;
};

const validateBankDetails = (form = {}) => {
  const invalidSortCode = BANK_DETAIL_FIELDS.find(
    (field) =>
      field.key.endsWith("SortCode") &&
      form[field.key] &&
      !/^\d{2}-\d{2}-\d{2}$/.test(form[field.key]),
  );

  if (invalidSortCode) {
    throw new Error(`${invalidSortCode.label} must be in 00-00-00 format.`);
  }

  const invalidAccountNumber = BANK_DETAIL_FIELDS.find(
    (field) =>
      field.key.endsWith("AccountNumber") &&
      form[field.key] &&
      !/^\d{8}$/.test(form[field.key]),
  );

  if (invalidAccountNumber) {
    throw new Error(`${invalidAccountNumber.label} must be 8 digits.`);
  }
};

const toIdList = (value) =>
  (Array.isArray(value) ? value : String(value || "").split(","))
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);

const toValueList = toIdList;

const hasSharedGroupId = (currentGroupIds = [], adviserGroupIds = []) => {
  if (!currentGroupIds.length) return false;
  const adviserGroupSet = new Set(
    adviserGroupIds.map((groupId) => groupId.toLowerCase()),
  );
  return currentGroupIds.some((groupId) =>
    adviserGroupSet.has(groupId.toLowerCase()),
  );
};

const groupRecipientValue = (groupId = "") =>
  groupId ? `group:${String(groupId).trim()}` : "";

const getEditForm = (
  rates = {},
  adviserEmail = "",
  adviserDob = null,
  adviserStatuses = [],
  adviserAppointmentDate = null,
  adviserLeftDate = null,
) => {
  const values = normaliseRates(rates);
  const savedStatementPassword = String(
    rates.statementPassword || "",
  ).replace(/\s+/g, "");

  const baseForm = EDIT_RATE_FIELDS.reduce(
    (form, field) => {
      let value =
        field.key === "notes"
          ? rates.notes || ""
          : field.key === "paidBy"
            ? rates.paidBy || adviserEmail
            : values[field.key];

      if (
        field.type === "percentage" &&
        value !== "" &&
        Number(value) <= 1
      ) {
        value = String(Number(value) * 100);
      }

      return {
        ...form,
        [field.key]: value,
        payingFor: Array.isArray(rates.payingFor) ? rates.payingFor : [],
      };
    },
    {},
  );

  const form = [
    ...BANK_DETAIL_FIELDS,
    ...PASSWORD_DETAIL_FIELDS,
    ...COMMISSION_SELECT_FIELDS,
  ].reduce(
    (form, field) => ({
      ...form,
      [field.key]: COMMISSION_SELECT_FIELDS.some((item) => item.key === field.key)
        ? (
            field.key === "emailCommissionStatementsTo" &&
            toValueList(rates[field.key]).length === 0
              ? [DEFAULT_STATEMENT_EMAIL]
              : toValueList(rates[field.key])
          )
        : field.key === "statementPassword"
          ? savedStatementPassword || statementPasswordFromDob(adviserDob)
          : rates[field.key] || "",
    }),
    baseForm,
  );

  return {
    ...form,
    monthlyFeeFromDate:
      toMonthlyFeeMonthValue(rates.monthlyFeeFromDate) ||
      toMonthlyFeeMonthValue(adviserAppointmentDate),
    monthlyFeeToDate:
      toMonthlyFeeMonthValue(rates.monthlyFeeToDate) ||
      toMonthlyFeeMonthValue(adviserLeftDate),
    monthlyFeePermission: resolveMonthlyFeePermission({
      savedPermission: rates.monthlyFeePermission,
      amount: rates.monthlyFees,
      statuses: adviserStatuses,
    }),
    emailCommissionStatementsCc: normaliseStatementCc(
      rates.emailCommissionStatementsCc,
    ),
  };
};

const getSavePayload = (form = {}) => {
  const ratePayload = EDIT_RATE_FIELDS.reduce((rates, field) => {
    const rawValue = form[field.key];

    if (field.type === "percentage") {
      return {
        ...rates,
        [field.key]: rawValue === "" ? "" : Number(rawValue) / 100,
      };
    }

    if (field.type === "number") {
      if (rawValue === "") return { ...rates, [field.key]: "" };

      const value = Number(rawValue);
      if (!Number.isFinite(value) || value < 0) {
        throw new Error("Monthly Fees must be zero or more.");
      }

      return { ...rates, [field.key]: value };
    }

    return { ...rates, [field.key]: String(rawValue ?? "").trim() };
  }, {});

  const bankPayload = BANK_DETAIL_FIELDS.reduce(
    (rates, field) => ({
      ...rates,
      [field.key]: String(
        formatBankDetailValue(field.key, form[field.key]) ?? "",
      ).trim(),
    }),
    ratePayload,
  );

  const passwordPayload = PASSWORD_DETAIL_FIELDS.reduce(
    (rates, field) => ({
      ...rates,
      [field.key]: String(form[field.key] || "").replace(/\s+/g, ""),
    }),
    bankPayload,
  );

  const commissionPayload = COMMISSION_SELECT_FIELDS.reduce(
    (rates, field) => ({
      ...rates,
      [field.key]: toValueList(form[field.key]),
    }),
    passwordPayload,
  );

  return {
    ...commissionPayload,
    monthlyFeeFromDate: toMonthlyFeeDateValue(form.monthlyFeeFromDate),
    monthlyFeeToDate: toMonthlyFeeDateValue(form.monthlyFeeToDate),
    monthlyFeePermission: String(form.monthlyFeePermission || "").trim(),
    emailCommissionStatementsCc: normaliseStatementCc(
      form.emailCommissionStatementsCc,
    ),
  };
};

export default function EditRates({
  adviserName,
  adviserId,
  adviserEmail,
  details,
  loading,
  onSaved,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedEditTab = searchParams.get("tab");
  const { userPermissions = [] } = useUserPermissions();
  const savedValues = useMemo(() => details?.rates || {}, [details]);
  const { advisers, loadingAdvisers } = useAdviserList();
  const [currentGroupIds, setCurrentGroupIds] = useState([]);
  const [currentGroupRecord, setCurrentGroupRecord] = useState(null);
  const [adviserGroupIdsByEmail, setAdviserGroupIdsByEmail] = useState({});
  const adviser = useMemo(() => details?.adviserDoc || {}, [details]);
  const application = useMemo(
    () => details?.applicationDetails || {},
    [details],
  );
  const adviserDob =
    details?.personalDetails?.dob ||
    details?.personalDetails?.dateOfBirth ||
    adviser?.dob ||
    adviser?.dateOfBirth ||
    null;
  const adviserAppointmentDate =
    adviser?.dateAppointed || application?.appointedAt || null;
  const adviserLeftDate = adviser?.dateLeft || null;
  const adviserStatuses = useMemo(() => asList(adviser.status), [adviser.status]);

  const [form, setForm] = useState(() =>
    getEditForm(
      savedValues,
      adviserEmail,
      adviserDob,
      adviserStatuses,
      adviserAppointmentDate,
      adviserLeftDate,
    ),
  );
  const [activeEditTab, setActiveEditTab] = useState(() =>
    EDIT_TABS.some((tab) => tab.id === requestedEditTab)
      ? requestedEditTab
      : "rates",
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const canAccessGroupDetails = canAccessDepartmentRoute(
    userPermissions,
    APP_ROUTES.GROUP_DETAILS,
  );
  const paidByOptions = useMemo(() => {
    const options = advisers.map((item) => {
      const adviserName = [
        item.preferredName || item.firstName,
        item.lastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();

      return {
        id: item.email,
        name: adviserName ? `${adviserName} | ${item.email}` : item.email,
      };
    });

    if (form.paidBy && !options.some((option) => option.id === form.paidBy)) {
      options.push({ id: form.paidBy, name: form.paidBy });
    }

    return options;
  }, [advisers, form.paidBy]);
  const adviserSelectOptions = useMemo(() => {
    const primaryGroupId = currentGroupIds[0] || "";
    const groupOptionValue = groupRecipientValue(primaryGroupId);
    const groupName =
      currentGroupRecord?.groupName ||
      currentGroupRecord?.group?.GroupName ||
      currentGroupRecord?.group?.groupName ||
      application.brandName ||
      application.tradingName ||
      application.tradingStyle ||
      adviser.brandName ||
      adviser.tradingName ||
      adviser.tradingStyle ||
      "";
    const values = [
      ...toValueList(form.sendCommissionPaymentsTo),
      ...toValueList(form.emailCommissionStatementsTo),
    ];
    const options = [
      groupOptionValue
        ? {
            id: groupOptionValue,
            name: groupName
              ? `${groupName} | Group ${primaryGroupId}`
              : `Group ${primaryGroupId}`,
          }
        : null,
      ...paidByOptions
        .filter((option) => {
          const optionEmail = String(option.id || "").trim().toLowerCase();
          const optionGroupIds = adviserGroupIdsByEmail[optionEmail] || [];

          return hasSharedGroupId(currentGroupIds, optionGroupIds);
        })
        .map((option) => ({ ...option })),
    ].filter(Boolean);

    values.forEach((value) => {
      if (!options.some((option) => option.id === value)) {
        options.push({ id: value, name: value });
      }
    });

    return options;
  }, [
    adviserGroupIdsByEmail,
    currentGroupIds,
    currentGroupRecord,
    adviser,
    application,
    form.emailCommissionStatementsTo,
    form.sendCommissionPaymentsTo,
    paidByOptions,
  ]);
  const groupBankDetails = currentGroupRecord?.bankDetails || {};

  useEffect(() => {
    return subscribeIODetails(
      adviserEmail,
      (ioDetails) => setCurrentGroupIds(toIdList(ioDetails?.groupId)),
      () => setCurrentGroupIds([]),
    );
  }, [adviserEmail]);

  useEffect(() => {
    let active = true;
    const primaryGroupId = currentGroupIds[0] || "";

    if (!primaryGroupId) {
      setCurrentGroupRecord(null);
      return undefined;
    }

    getSavedGroupDetails(primaryGroupId)
      .then((groupRecord) => {
        if (active) setCurrentGroupRecord(groupRecord);
      })
      .catch((error) => {
        console.error("Failed to load current group details:", error);
        if (active) setCurrentGroupRecord(null);
      });

    return () => {
      active = false;
    };
  }, [currentGroupIds]);

  useEffect(() => {
    let active = true;

    const loadAdviserGroups = async () => {
      const entries = await Promise.all(
        advisers
          .filter((item) => item.email)
          .map(async (item) => {
            const email = String(item.email || "").trim();
            const snapshot = await getDoc(getIODetailsRef(email));
            const ioDetails = snapshot.exists() ? snapshot.data() : {};

            return [email.toLowerCase(), toIdList(ioDetails?.groupId)];
          }),
      );

      if (active) {
        setAdviserGroupIdsByEmail(Object.fromEntries(entries));
      }
    };

    if (!advisers.length) {
      setAdviserGroupIdsByEmail({});
      return undefined;
    }

    loadAdviserGroups().catch((error) => {
      console.error("Failed to load adviser IO group IDs:", error);
      if (active) setAdviserGroupIdsByEmail({});
    });

    return () => {
      active = false;
    };
  }, [advisers]);

  useEffect(() => {
    setForm(
      getEditForm(
        savedValues,
        adviserEmail,
        adviserDob,
        adviserStatuses,
        adviserAppointmentDate,
        adviserLeftDate,
      ),
    );
  }, [
    adviserAppointmentDate,
    adviserLeftDate,
    adviserDob,
    adviserEmail,
    adviserStatuses,
    savedValues,
  ]);

  useEffect(() => {
    if (EDIT_TABS.some((tab) => tab.id === requestedEditTab)) {
      setActiveEditTab(requestedEditTab);
    }
  }, [requestedEditTab]);

  const handleEditTabChange = (tabId) => {
    setActiveEditTab(tabId);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("tab", tabId);
    setSearchParams(nextSearchParams, { replace: true });
  };

  useEffect(() => {
    let active = true;

    loadPayingForRows(adviserEmail, adviserId)
      .then((rows) => {
        if (active) {
          setForm((currentForm) => ({
            ...currentForm,
            payingFor: rows,
          }));
        }
      })
      .catch((error) => {
        console.error("Failed to load paying-for advisers:", error);
      });

    return () => {
      active = false;
    };
  }, [adviserEmail, adviserId]);

  const totalMonthlyFees = useMemo(() => {
    return calculateTotalMonthlyFees({
      monthlyFees: form.monthlyFees,
      paidBy: form.paidBy,
      adviserEmail,
      payingFor: form.payingFor,
    });
  }, [
    adviserEmail,
    form.monthlyFees,
    form.paidBy,
    form.payingFor,
  ]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError("");

      if (!adviserId) {
        throw new Error("Adviser ID not found.");
      }

      validateBankDetails(form);
      if (!isValidStatementCc(form.emailCommissionStatementsCc)) {
        throw new Error(
          "CC email addresses must be valid and separated by commas.",
        );
      }

      await saveAdviserRates(adviserId, {
        ...getSavePayload(form),
        payingFor: form.payingFor,
        totalMonthlyFeesCharged: totalMonthlyFees,
      });
      onSaved?.();
    } catch (error) {
      console.error("Failed to save adviser rates:", error);
      setSaveError(error.message || "Failed to save adviser rates.");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenGroupBankDetails = () => {
    const primaryGroupId = currentGroupIds[0] || "";
    if (!primaryGroupId) return;

    navigate(
      `/GroupDetails?groupId=${encodeURIComponent(primaryGroupId)}&tab=bankDetails`,
    );
  };

  return (
    <div className="smFeed">
      <Breadcrumb
        items={[
          { label: "Advisers", to: "/advisers" },
          {
            label: adviserEmail || "Adviser",
            to: `/advisers/${encodeURIComponent(adviserEmail)}?tab=rates`,
          },
          { label: "Edit IFA Rates" },
        ]}
      />

      <div className="smEditActions">
        <div className="smEditActionsLeft">
          <div className="smNameRow">
            <h3 className="FP_H3">{adviserName || "Edit IFA Rates"}</h3>
            {adviser.isAR ? <span className="smBadge">AR</span> : null}
            {adviser.isIndroducerAR ? (
              <span className="smBadge">IAR</span>
            ) : null}
          </div>
        </div>
        <PrimaryButton
          label={saving ? "Saving..." : "Save Details"}
          onClick={handleSave}
          disabled={saving || loading}
        />
      </div>

      {saveError ? (
        <div className="Content-Small adviserLicenseError">{saveError}</div>
      ) : null}

      <div className="smRatesEditLayout">
        <div className="fit-column gap-s">
          <Tabs
            tabs={EDIT_TABS}
            activeTab={activeEditTab}
            onTabChange={handleEditTabChange}
          />

          <div>
            {activeEditTab === "rates" ? (
              <PostCard title="IFA Rates">
                <div className="smGrid2">
                  {EDIT_RATE_FIELDS.filter((field) => field.key !== "notes").map(
                    (field) => (
                      field.key === "paidBy" ? (
                        <CustomSelect
                          key={field.key}
                          label="Paid By"
                          options={paidByOptions}
                          value={form.paidBy}
                          placeholder={
                            loadingAdvisers ? "Loading advisers..." : "Select adviser"
                          }
                          disabled={loadingAdvisers}
                          searchable
                          clearable={false}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              paidBy: event.target.value,
                            }))
                          }
                        />
                      ) : field.key === "monthlyFees" ? (
                        <React.Fragment key={field.key}>
                          <CustomSelect
                            label="Monthly Fee Permission"
                            options={MONTHLY_FEE_OPTIONS}
                            value={form.monthlyFeePermission}
                            placeholder="Select permission"
                            searchable={false}
                            onChange={(event) => {
                              const permission = event.target.value;
                              setForm((current) => ({
                                ...current,
                                monthlyFeePermission: permission,
                                monthlyFees:
                                  monthlyFeeAmountForPermission(permission),
                              }));
                            }}
                          />
                          <Field
                            label="Monthly Fees (£)"
                            type="number"
                            min="0"
                            step="any"
                            value={form.monthlyFees}
                            onChange={(value) =>
                              setForm((current) => ({
                                ...current,
                                monthlyFees: value,
                              }))
                            }
                          />
                          <Field
                            label="Monthly Fees From Date"
                            type="month"
                            value={form.monthlyFeeFromDate}
                            onChange={(value) =>
                              setForm((current) => ({
                                ...current,
                                monthlyFeeFromDate: value,
                              }))
                            }
                          />
                          <Field
                            label="Monthly Fees To Date"
                            type="month"
                            value={form.monthlyFeeToDate}
                            onChange={(value) =>
                              setForm((current) => ({
                                ...current,
                                monthlyFeeToDate: value,
                              }))
                            }
                          />
                        </React.Fragment>
                      ) : (
                        <Field
                          key={field.key}
                          label={
                            field.key === "monthlyFees"
                              ? "Monthly Fees (£)"
                              : field.type === "percentage"
                                ? `${field.label} %`
                                : field.label
                          }
                          type={
                            field.type === "percentage" || field.type === "number"
                              ? "number"
                              : "text"
                          }
                          min={
                            field.type === "percentage" || field.type === "number"
                              ? "0"
                              : undefined
                          }
                          step={field.type === "number" ? "any" : undefined}
                          value={form[field.key]}
                          onChange={(value) =>
                            setForm((current) => ({ ...current, [field.key]: value }))
                          }
                        />
                      )
                    ),
                  )}
                  <Field
                    label="Total Monthly Fees Charged (£)"
                    type="number"
                    value={totalMonthlyFees}
                    onChange={() => {}}
                    readOnly
                  />
                  <div/>
                  <Field
                    label="Notes"
                    type="textarea"
                    value={form.notes}
                    placeholder="Additional Notes"
                    onChange={(value) =>
                      setForm((current) => ({ ...current, notes: value }))
                    }
                  />
                </div>

                {form.payingFor.length ? (
                  <table>
                    <thead>
                      <tr>
                        <th className="FP_Help_Text_Semibold">Paying For</th>
                        <th className="FP_Help_Text_Semibold">Monthly Fees (£)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.payingFor.map((person, index) => (
                        <tr key={person?.adviserId || person?.email || index}>
                          <td className="FP_Help_Text">
                            {person?.adviserId || person?.email ? (
                              <Link
                                className="table-url-link"
                                to={`/advisers/${encodeURIComponent(
                                  person.adviserId || person.email,
                                )}`}
                              >
                                {[person?.name, person?.email]
                                  .filter(Boolean)
                                  .join(" | ") || "-"}
                              </Link>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="FP_Help_Text">
                            £
                            {Number(
                              person?.monthlyFee ?? form.monthlyFees ?? 0,
                            ).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : null}
              </PostCard>
            ) : (
              <PostCard title="Bank Details">
                <div className="smBankDetailsForm">
                  <div className="smBankDetailsSection">
                    <div className="FP_Body_Semibold">Adviser Bank Details</div>
                    {BANK_DETAIL_FIELDS.slice(0, 3).map((field) => (
                      <Field
                        key={field.key}
                        label={field.label}
                        type="text"
                        value={form[field.key]}
                        placeholder={field.placeholder}
                        full={field.full}
                        maxLength={field.maxLength}
                        pattern={field.pattern}
                        inputMode={field.inputMode}
                        onChange={(value) =>
                          setForm((current) => ({
                            ...current,
                            [field.key]: formatBankDetailValue(
                              field.key,
                              value,
                            ),
                          }))
                        }
                      />
                    ))}
                  </div>

                  <div className="smBankDetailsSection">
                    <div className="smSectionHeaderInline">
                      <div className="FP_Body_Semibold">Group Bank Details</div>
                      {currentGroupIds[0] && canAccessGroupDetails ? (
                        <ButtonTextLink
                          label="Open bank details"
                          onClick={handleOpenGroupBankDetails}
                        />
                      ) : null}
                    </div>
                    {BANK_DETAIL_FIELDS.slice(3).map((field) => (
                      <Field
                        key={field.key}
                        label={field.label}
                        type="text"
                        value={groupBankDetails[field.key] || form[field.key]}
                        placeholder={field.placeholder}
                        full={field.full}
                        maxLength={field.maxLength}
                        pattern={field.pattern}
                        inputMode={field.inputMode}
                        readOnly
                        onChange={(value) =>
                          setForm((current) => ({
                            ...current,
                            [field.key]: formatBankDetailValue(
                              field.key,
                              value,
                            ),
                          }))
                        }
                      />
                    ))}
                    <Field
                      label="Statement Details To"
                      type="text"
                      value={groupBankDetails.emailCommissionStatementsTo || ""}
                      placeholder="No group statement recipient saved"
                      full
                      readOnly
                      onChange={() => {}}
                    />
                    <Field
                      label="Statement Details CC"
                      type="text"
                      value={groupBankDetails.emailCommissionStatementsCc || ""}
                      placeholder="No group CC recipients saved"
                      full
                      readOnly
                      onChange={() => {}}
                    />
                  </div>

                  <div className="smBankDetailsSection">
                    <div className="FP_Body_Semibold">Statement Password</div>
                    <Field
                      label="Statement Password"
                      type="password"
                      showPasswordToggle
                      value={form.statementPassword}
                      placeholder="Enter statement password"
                      full
                      onChange={(value) =>
                        setForm((current) => ({
                          ...current,
                          statementPassword: value,
                        }))
                      }
                    />
                  </div>

                  <div className="smBankDetailsSection">
                    <div className="FP_Body_Semibold">
                      Send Commission Payments to Other Adviser
                    </div>
                  {COMMISSION_SELECT_FIELDS.map((field) => (
                    <CheckboxMultiSelect
                      key={field.key}
                      label={field.label}
                      options={adviserSelectOptions.map((option) => ({
                        value: option.id,
                        label: option.name,
                      }))}
                      value={toValueList(form[field.key])}
                      placeholder={
                        loadingAdvisers ? "Loading advisers..." : field.placeholder
                      }
                      helperText="Select one or more recipients"
                      disabled={loadingAdvisers}
                      onChange={(value) =>
                        setForm((current) => ({
                          ...current,
                          [field.key]: value,
                        }))
                      }
                    />
                  ))}
                    <Field
                      label="CC"
                      type="text"
                      value={form.emailCommissionStatementsCc}
                      placeholder="Email addresses separated by commas"
                      full
                      onChange={(value) =>
                        setForm((current) => ({
                          ...current,
                          emailCommissionStatementsCc: value,
                        }))
                      }
                    />
                  </div>
                </div>
              </PostCard>
            )}
          </div>
        </div>

        <AdviserDetailsCard adviser={adviser} application={application} />
      </div>
      <RatesEditLogs adviserId={adviserId} activeTab={activeEditTab} />
    </div>
  );
}
